import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ChevronRight,
  FileText,
  Folder,
  Home,
  MoreHorizontal,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { BrandSiteCollectionSettingsSheet } from "@/components/brand-site/BrandSiteCollectionSettingsSheet";
import { BrandSiteCoverField } from "@/components/brand-site/BrandSiteCoverField";
import { BrandSiteItemHtmlField } from "@/components/brand-site/BrandSiteItemHtmlField";
import { useHermes } from "@/components/hermes/HermesProvider";
import {
  AdminAlert,
  AdminEmptyState,
  AdminPageHeader,
} from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useAccountWorkspace } from "@/lib/account-workspace";
import {
  BrandSiteApiError,
  brandSiteAssetBaseUrl,
  brandSiteCanVisit,
  brandSiteFrontendPageUrl,
  listBrandSiteCollectionItems,
  listBrandSiteCollections,
  listBrandSitePages,
  updateBrandSitePage,
  upsertBrandSiteCollectionItem,
  type BrandSiteCollection,
  type BrandSiteCollectionItem,
  type BrandSitePage,
} from "@/lib/brand-site-api";
import {
  buildContentTree,
  type ContentTreeNode,
} from "@/lib/brand-site-content-tree";
import {
  listSharedPartials,
  type SharedPartialRow,
} from "@/lib/brand-site-shared-partials";
import {
  formValuesToSettings,
  isHtmlItemSchemaField,
  parseItemSchemaFromTemplate,
  schemaToFormFields,
  settingsToFormValues,
  type ItemSchemaFormField,
} from "@/lib/brand-site-item-schema";
import {
  cwdFromFolderNode,
  explorerPathSegments,
  filterExplorerEntries,
  isExplorerFolder,
  isExplorerOpenable,
  listExplorerEntries,
  parentExplorerCwd,
  pushExplorerHistory,
  sameExplorerCwd,
  type ExplorerCwd,
} from "@/lib/brand-site-pages-explorer";
import { useRefreshBrandSitePagesOnHermesDraft } from "@/lib/use-refresh-brand-site-pages-on-hermes-draft";
import { cn } from "@/lib/utils";
import { useBrandSiteWorkbench } from "@/pages/brand-site/BrandSiteSiteLayout";

type EditorTarget =
  | { kind: "static"; page: BrandSitePage }
  | { kind: "item"; collection: BrandSiteCollection; item: BrandSiteCollectionItem };

type PagesView = "site" | "shared";

type ContextMenuState = {
  x: number;
  y: number;
  node: ContentTreeNode;
};

/** 页面栏：访达式浏览 + Sheet 编辑（静态页 / 栏目文章） */
export default function BrandSitePagesPage() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { currentSite } = useBrandSiteWorkbench();
  const { accountId } = useAccountWorkspace();
  const [pages, setPages] = useState<BrandSitePage[]>([]);
  const [collections, setCollections] = useState<BrandSiteCollection[]>([]);
  const [itemsByCollectionId, setItemsByCollectionId] = useState<
    Record<number, BrandSiteCollectionItem[]>
  >({});
  const [view, setView] = useState<PagesView>("site");
  const [query, setQuery] = useState("");
  const [history, setHistory] = useState<{ stack: ExplorerCwd[]; index: number }>({
    stack: [{ kind: "root" }],
    index: 0,
  });
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const [collectionSettings, setCollectionSettings] = useState<BrandSiteCollection | null>(null);
  const [renameTarget, setRenameTarget] = useState<BrandSitePage | null>(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ctxMenu, setCtxMenu] = useState<ContextMenuState | null>(null);
  const { isSending, brandSiteDraftRevision } = useHermes();

  const cwd = history.stack[history.index] ?? { kind: "root" as const };
  const homeSlug = pages.find((p) => p.is_home)?.slug ?? pages[0]?.slug ?? "index";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pagesPayload, collectionsPayload] = await Promise.all([
        listBrandSitePages(accountId, currentSite.id),
        listBrandSiteCollections(accountId, currentSite.id).catch(() => ({
          collections: [] as BrandSiteCollection[],
        })),
      ]);
      const nextPages = pagesPayload.pages ?? [];
      const nextCollections = collectionsPayload.collections ?? [];
      setPages(nextPages);
      setCollections(nextCollections);

      const entries = await Promise.all(
        nextCollections.map(async (collection) => {
          try {
            const payload = await listBrandSiteCollectionItems(
              accountId,
              currentSite.id,
              collection.id,
            );
            return [collection.id, payload.items ?? []] as const;
          } catch {
            return [collection.id, [] as BrandSiteCollectionItem[]] as const;
          }
        }),
      );
      setItemsByCollectionId(Object.fromEntries(entries));
    } catch (caught) {
      setError(caught instanceof BrandSiteApiError ? caught.message : "无法加载页面");
      setPages([]);
      setCollections([]);
      setItemsByCollectionId({});
    } finally {
      setLoading(false);
    }
  }, [accountId, currentSite.id]);

  useEffect(() => {
    void load();
  }, [load]);

  useRefreshBrandSitePagesOnHermesDraft({
    enabled: true,
    isSending,
    brandSiteDraftRevision,
    refresh: load,
  });

  // Hermes 写条目后刷新选中编辑态
  useEffect(() => {
    if (editor?.kind !== "item") return;
    const list = itemsByCollectionId[editor.collection.id] ?? [];
    const fresh = list.find((row) => row.id === editor.item.id);
    if (!fresh) return;
    if (JSON.stringify(fresh.settings) === JSON.stringify(editor.item.settings)) return;
    setEditor({ kind: "item", collection: editor.collection, item: fresh });
  }, [itemsByCollectionId, editor]);

  useEffect(() => {
    if (!ctxMenu) return;
    function close() {
      setCtxMenu(null);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }
    window.addEventListener("click", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [ctxMenu]);

  const tree = useMemo(
    () => buildContentTree(pages, homeSlug, collections, itemsByCollectionId),
    [pages, homeSlug, collections, itemsByCollectionId],
  );

  const layerEntries = useMemo(() => {
    const raw = listExplorerEntries(tree, cwd);
    return filterExplorerEntries(raw, query);
  }, [tree, cwd, query]);

  const sharedRows = useMemo(() => {
    const rows = listSharedPartials(pages);
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return rows;
    return rows.filter(
      (row) =>
        row.title.toLowerCase().includes(normalizedQuery) ||
        row.callName.toLowerCase().includes(normalizedQuery) ||
        row.path.toLowerCase().includes(normalizedQuery),
    );
  }, [pages, query]);

  const pathSegments = useMemo(() => explorerPathSegments(cwd), [cwd]);

  const base = brandId
    ? `/accounts/${accountId}/brands/${brandId}/apps/brand_site/sites/${currentSite.id}`
    : "";

  function navigateTo(next: ExplorerCwd) {
    setHistory((current) => pushExplorerHistory(current.stack, current.index, next));
    setSelectedKey(null);
    setQuery("");
  }

  function goBack() {
    setHistory((current) => {
      if (current.index <= 0) return current;
      return { ...current, index: current.index - 1 };
    });
    setSelectedKey(null);
  }

  function goForward() {
    setHistory((current) => {
      if (current.index >= current.stack.length - 1) return current;
      return { ...current, index: current.index + 1 };
    });
    setSelectedKey(null);
  }

  function goUp() {
    const parent = parentExplorerCwd(cwd);
    if (!parent) return;
    navigateTo(parent);
  }

  function resolveEditor(node: ContentTreeNode): EditorTarget | null {
    if (node.kind === "static" && node.pageId != null) {
      const page = pages.find((p) => p.id === node.pageId);
      return page ? { kind: "static", page } : null;
    }
    if (node.kind === "collection_item" && node.collectionId != null && node.itemId != null) {
      const collection = collections.find((c) => c.id === node.collectionId);
      const item = (itemsByCollectionId[node.collectionId] ?? []).find((row) => row.id === node.itemId);
      if (collection && item) return { kind: "item", collection, item };
    }
    return null;
  }

  function openCollectionSettings(node: ContentTreeNode) {
    if (node.kind !== "collection_folder" || node.collectionId == null) return;
    const collection = collections.find((c) => c.id === node.collectionId);
    if (collection) setCollectionSettings(collection);
  }

  function openEditor(node: ContentTreeNode) {
    const target = resolveEditor(node);
    if (!target) return;
    if (target.kind === "static") {
      navigate(
        `${base}/edit?page=${encodeURIComponent(target.page.slug)}&source=1&fullscreen=1`,
      );
      return;
    }
    setEditor(target);
  }

  function openSharedEditor(row: SharedPartialRow) {
    const page = pages.find((item) => item.id === row.pageId);
    if (!page) return;
    navigate(`${base}/edit?page=${encodeURIComponent(page.slug)}&source=1&fullscreen=1`);
  }

  function openRenameDialog(row: SharedPartialRow) {
    const page = pages.find((item) => item.id === row.pageId);
    if (!page) return;
    setRenameTarget(page);
    setRenameTitle(page.title.trim() || row.callName);
  }

  async function renameSharedPage() {
    if (!renameTarget) return;
    const title = renameTitle.trim();
    if (!title) {
      toast.error("名称不能为空");
      return;
    }
    setRenaming(true);
    try {
      const payload = await updateBrandSitePage(
        accountId,
        currentSite.id,
        renameTarget.id,
        { title },
      );
      setPages((current) =>
        current.map((page) => (page.id === payload.page.id ? payload.page : page)),
      );
      setEditor((current) =>
        current?.kind === "static" && current.page.id === payload.page.id
          ? { kind: "static", page: payload.page }
          : current,
      );
      setRenameTarget(null);
      toast.success("名称已更新");
    } catch (caught) {
      toast.error(caught instanceof BrandSiteApiError ? caught.message : "重命名失败，请稍后重试");
    } finally {
      setRenaming(false);
    }
  }

  function handleActivate(node: ContentTreeNode) {
    if (isExplorerFolder(node)) {
      const next = cwdFromFolderNode(node);
      if (next) navigateTo(next);
      return;
    }
    if (isExplorerOpenable(node)) openEditor(node);
  }

  async function copyPath(node: ContentTreeNode) {
    try {
      await navigator.clipboard.writeText(node.path);
      toast.success("已复制路径");
    } catch {
      toast.error("复制失败");
    }
  }

  function openFrontend(node: ContentTreeNode) {
    if (node.path.startsWith("/_")) {
      toast.message("公共部分没有前台页面");
      return;
    }
    if (!brandSiteCanVisit(currentSite)) {
      toast.message("发布后才能从前端访问");
      return;
    }
    const url = brandSiteFrontendPageUrl(currentSite, node.path, { preferCustomHost: true });
    if (!url) {
      toast.error("还没有可访问的前台地址");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }

  function openInDesign(node: ContentTreeNode) {
    if (!base) return;
    if (node.kind === "static" && node.slug) {
      navigate(`${base}/edit?page=${encodeURIComponent(node.slug)}`);
      return;
    }
    if (node.kind === "collection_folder" && node.slug) {
      navigate(`${base}/edit?page=${encodeURIComponent(`${node.slug}*`)}`);
      return;
    }
    if (node.kind === "collection_item" && node.slug && node.collectionId != null) {
      const collection = collections.find((c) => c.id === node.collectionId);
      if (collection) {
        navigate(`${base}/edit?page=${encodeURIComponent(`${collection.key}/:slug`)}`);
      }
    }
  }

  const canBack = history.index > 0;
  const canForward = history.index < history.stack.length - 1;
  const canUp = parentExplorerCwd(cwd) != null;

  const folderEmpty =
    cwd.kind === "collection" &&
    listExplorerEntries(tree, cwd).length === 0 &&
    !query.trim();

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <AdminPageHeader
        title="页面"
        description={
          view === "site"
            ? "像文件夹一样浏览：双击进入栏目或打开编辑；栏目可右键「栏目设置」改字段与模板。"
            : "集中查看和编辑会在多个页面重复使用的页头、页脚和全站样式。"
        }
      />

      {error ? <AdminAlert>{error}</AdminAlert> : null}
      {loading ? <p className="text-sm text-muted-foreground">加载中…</p> : null}

      {!loading ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card">
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
            <div className="flex items-center gap-1">
              <Button
                type="button"
                size="sm"
                variant={view === "site" ? "default" : "outline"}
                className="h-8"
                onClick={() => {
                  setView("site");
                  setSelectedKey(null);
                  setCtxMenu(null);
                  setQuery("");
                }}
              >
                站点页面
              </Button>
              <Button
                type="button"
                size="sm"
                variant={view === "shared" ? "default" : "outline"}
                className="h-8"
                onClick={() => {
                  setView("shared");
                  setSelectedKey(null);
                  setCtxMenu(null);
                  setQuery("");
                }}
              >
                公共部分
              </Button>
            </div>

            {view === "site" ? (
              <>
                <div className="flex items-center gap-0.5">
                  <IconNavButton label="后退" disabled={!canBack} onClick={goBack}>
                    <ArrowLeft className="size-3.5" />
                  </IconNavButton>
                  <IconNavButton label="前进" disabled={!canForward} onClick={goForward}>
                    <ArrowRight className="size-3.5" />
                  </IconNavButton>
                  <IconNavButton label="上级" disabled={!canUp} onClick={goUp}>
                    <ArrowUp className="size-3.5" />
                  </IconNavButton>
                </div>

                <nav
                  className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto rounded-md bg-muted/40 px-2 py-1 text-sm"
                  aria-label="当前路径"
                >
                  {pathSegments.map((seg, index) => (
                    <div key={explorerSegKey(seg.cwd)} className="flex min-w-0 items-center">
                      {index > 0 ? (
                        <ChevronRight className="mx-0.5 size-3.5 shrink-0 text-muted-foreground" />
                      ) : null}
                      <button
                        type="button"
                        className={cn(
                          "max-w-40 truncate rounded px-1.5 py-0.5 hover:bg-muted",
                          index === pathSegments.length - 1
                            ? "font-medium text-foreground"
                            : "text-muted-foreground",
                        )}
                        onClick={() => {
                          if (!sameExplorerCwd(seg.cwd, cwd)) navigateTo(seg.cwd);
                        }}
                      >
                        {seg.name}
                      </button>
                    </div>
                  ))}
                </nav>
              </>
            ) : (
              <div className="min-w-0 flex-1" />
            )}
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={view === "site" ? "搜索当前层…" : "搜索公共部分…"}
              className="h-8 w-full max-w-56 sm:w-44"
              aria-label="搜索"
            />
            {view === "site" && cwd.kind === "collection" ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => {
                  const collection = collections.find((c) => c.id === cwd.collectionId);
                  if (collection) setCollectionSettings(collection);
                }}
              >
                栏目设置
              </Button>
            ) : null}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {view === "shared" ? (
              sharedRows.length === 0 ? (
                <div className="p-6">
                  <AdminEmptyState>
                    {query.trim() ? (
                      <p>没有匹配的公共部分。</p>
                    ) : (
                      <p>
                        还没有公共部分。可在对话里说：抽出页头，或建默认的页头、页脚、全站样式。
                      </p>
                    )}
                  </AdminEmptyState>
                </div>
              ) : (
                <ul className="divide-y divide-border" role="list">
                  {sharedRows.map((row) => (
                    <li key={row.pageId}>
                      <div
                        role="button"
                        tabIndex={0}
                        className="flex w-full cursor-default items-center gap-3 px-4 py-2.5 text-left outline-none hover:bg-muted/50 focus-visible:bg-accent/80"
                        onClick={() => openSharedEditor(row)}
                        onDoubleClick={() => openSharedEditor(row)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") openSharedEditor(row);
                        }}
                      >
                        <FileText className="size-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-foreground">{row.title}</span>
                          <span className="block truncate font-mono text-[11px] text-muted-foreground">
                            {row.callName} · {row.path}
                          </span>
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              className="shrink-0"
                              aria-label="更多"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <MoreHorizontal className="size-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
                            <DropdownMenuItem onClick={() => openSharedEditor(row)}>
                              打开编辑
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openRenameDialog(row)}>
                              重命名
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                toast.info("改这里会作用到所有引用了该公共部分的页面")
                              }
                            >
                              查看影响说明
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </li>
                  ))}
                </ul>
              )
            ) : (
              <>
                {layerEntries.length === 0 ? (
                  <div className="p-6">
                    {folderEmpty ? (
                      <AdminEmptyState>
                        <p className="font-medium text-foreground">还没有文章</p>
                        <p className="mt-1">可去设计页对话写一篇，或在此栏目发布后回来。</p>
                        <Button asChild variant="outline" size="sm" className="mt-4">
                          <Link to={`${base}/edit`}>去设计页</Link>
                        </Button>
                      </AdminEmptyState>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {query.trim() ? "没有匹配的项。" : "这一层是空的。"}
                      </p>
                    )}
                  </div>
                ) : (
                  <ul className="divide-y divide-border" role="list">
                    {layerEntries.map((node) => {
                      const Icon = node.isHome ? Home : isExplorerFolder(node) ? Folder : FileText;
                      const active = selectedKey === node.key;
                      return (
                        <li key={node.key}>
                          <div
                            role="button"
                            tabIndex={0}
                            aria-selected={active}
                            className={cn(
                              "flex w-full cursor-default items-center gap-3 px-4 py-2.5 text-left outline-none",
                              active ? "bg-accent/80" : "hover:bg-muted/50",
                            )}
                            onClick={() => setSelectedKey(node.key)}
                            onDoubleClick={() => handleActivate(node)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") handleActivate(node);
                            }}
                            onContextMenu={(event) => {
                              event.preventDefault();
                              setSelectedKey(node.key);
                              setCtxMenu({ x: event.clientX, y: event.clientY, node });
                            }}
                          >
                            <Icon className="size-4 shrink-0 text-muted-foreground" />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm text-foreground">{node.title}</span>
                              <span className="block truncate font-mono text-[11px] text-muted-foreground">
                                {node.path}
                              </span>
                            </span>
                            {isExplorerFolder(node) ? (
                              <span className="shrink-0 text-[11px] text-muted-foreground">
                                {node.empty ? "空" : `${node.children.length} 篇`}
                              </span>
                            ) : null}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon-sm"
                                  className="shrink-0"
                                  aria-label="更多"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <MoreHorizontal className="size-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                {isExplorerFolder(node) ? (
                                  <>
                                    <DropdownMenuItem onClick={() => handleActivate(node)}>
                                      打开
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openCollectionSettings(node)}>
                                      栏目设置
                                    </DropdownMenuItem>
                                  </>
                                ) : (
                                  <DropdownMenuItem onClick={() => openEditor(node)}>
                                    打开编辑
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => openInDesign(node)}>
                                  在设计页预览
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openFrontend(node)}>
                                  前端访问
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => void copyPath(node)}>
                                  复制路径
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-muted-foreground">
            <span>共 {view === "shared" ? sharedRows.length : layerEntries.length} 项</span>
            <span>{view === "shared" ? "单击打开 · 更多操作" : "双击打开 · 右键菜单"}</span>
          </div>
        </div>
      ) : null}

      {ctxMenu ? (
        <div
          className="fixed z-50 min-w-40 rounded-md border border-border bg-popover p-1 text-sm shadow-md"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          role="menu"
          onClick={(event) => event.stopPropagation()}
        >
          {isExplorerFolder(ctxMenu.node) ? (
            <>
              <CtxItem
                onClick={() => {
                  handleActivate(ctxMenu.node);
                  setCtxMenu(null);
                }}
              >
                打开
              </CtxItem>
              <CtxItem
                onClick={() => {
                  openCollectionSettings(ctxMenu.node);
                  setCtxMenu(null);
                }}
              >
                栏目设置
              </CtxItem>
            </>
          ) : (
            <CtxItem
              onClick={() => {
                openEditor(ctxMenu.node);
                setCtxMenu(null);
              }}
            >
              打开编辑
            </CtxItem>
          )}
          <CtxItem
            onClick={() => {
              openInDesign(ctxMenu.node);
              setCtxMenu(null);
            }}
          >
            在设计页预览
          </CtxItem>
          <CtxItem
            onClick={() => {
              openFrontend(ctxMenu.node);
              setCtxMenu(null);
            }}
          >
            前端访问
          </CtxItem>
          <CtxItem
            onClick={() => {
              void copyPath(ctxMenu.node);
              setCtxMenu(null);
            }}
          >
            复制路径
          </CtxItem>
        </div>
      ) : null}

      <Dialog
        open={renameTarget != null}
        onOpenChange={(open) => {
          if (!open && !renaming) setRenameTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>重命名公共部分</DialogTitle>
            <DialogDescription>修改显示名称，不会改变调用名或路径。</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="shared-page-title">名称</Label>
            <Input
              id="shared-page-title"
              value={renameTitle}
              onChange={(event) => setRenameTitle(event.target.value)}
              disabled={renaming}
              autoFocus
              onKeyDown={(event) => {
                if (event.key === "Enter") void renameSharedPage();
              }}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={renaming}
              onClick={() => setRenameTarget(null)}
            >
              取消
            </Button>
            <Button type="button" disabled={renaming} onClick={() => void renameSharedPage()}>
              {renaming ? "保存中…" : "保存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BrandSiteCollectionSettingsSheet
        open={collectionSettings != null}
        onOpenChange={(open) => !open && setCollectionSettings(null)}
        accountId={accountId}
        brandSiteId={currentSite.id}
        collection={collectionSettings}
        designBase={base}
        onSaved={(updated) => {
          setCollections((current) =>
            current.map((row) => (row.id === updated.id ? updated : row)),
          );
          setCollectionSettings(updated);
        }}
      />

      <Sheet open={editor != null} onOpenChange={(open) => !open && setEditor(null)}>
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto sm:max-w-xl md:max-w-2xl"
        >
          {editor?.kind === "item" ? (
            <>
              <SheetHeader className="border-b border-border text-left">
                <SheetTitle>
                  {typeof editor.item.settings.title === "string" && editor.item.settings.title
                    ? String(editor.item.settings.title)
                    : editor.item.slug}
                </SheetTitle>
                <SheetDescription className="font-mono">{editor.item.url}</SheetDescription>
              </SheetHeader>
              <div className="p-4">
                <ItemSchemaForm
                  accountId={accountId}
                  brandId={Number(brandId) || 0}
                  brandSiteId={currentSite.id}
                  assetBaseUrl={brandSiteAssetBaseUrl(currentSite)}
                  publicUrl={currentSite.public_url ?? null}
                  collection={editor.collection}
                  item={editor.item}
                  compact
                  onSaved={(updated) => {
                    setItemsByCollectionId((current) => {
                      const list = current[editor.collection.id] ?? [];
                      return {
                        ...current,
                        [editor.collection.id]: list.map((row) =>
                          row.id === updated.id ? updated : row,
                        ),
                      };
                    });
                    setEditor({
                      kind: "item",
                      collection: editor.collection,
                      item: updated,
                    });
                  }}
                />
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function explorerSegKey(cwd: ExplorerCwd): string {
  return cwd.kind === "root" ? "root" : `c:${cwd.collectionId}`;
}

function IconNavButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      disabled={disabled}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function CtxItem({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className="flex w-full rounded-sm px-2 py-1.5 text-left hover:bg-accent"
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function ItemSchemaForm({
  accountId,
  brandId,
  brandSiteId,
  assetBaseUrl,
  publicUrl,
  collection,
  item,
  onSaved,
  compact = false,
}: {
  accountId: string | number;
  brandId: number;
  brandSiteId: number;
  assetBaseUrl: string | null;
  publicUrl: string | null;
  collection: BrandSiteCollection;
  item: BrandSiteCollectionItem;
  onSaved: (item: BrandSiteCollectionItem) => void;
  compact?: boolean;
}) {
  const fields = useMemo(
    () =>
      schemaToFormFields(
        collection.item_schema ?? parseItemSchemaFromTemplate(collection.item_template_html),
      ),
    [collection.item_schema, collection.item_template_html],
  );
  const [values, setValues] = useState(() => settingsToFormValues(fields, item.settings));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    setValues(settingsToFormValues(fields, item.settings));
    setFormError(null);
  }, [fields, item.id, item.settings]);

  async function handleSave() {
    setSaving(true);
    setFormError(null);
    try {
      const settings = formValuesToSettings(fields, values);
      const payload = await upsertBrandSiteCollectionItem(accountId, brandSiteId, collection.id, {
        slug: item.slug,
        settings,
      });
      onSaved(payload.item);
      toast.success("已保存并发布");
    } catch (caught) {
      const message =
        caught instanceof BrandSiteApiError ? caught.message : "保存失败，请稍后重试";
      setFormError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  const itemTitle =
    typeof item.settings.title === "string" && item.settings.title
      ? String(item.settings.title)
      : item.slug;

  return (
    <div className={cn("space-y-4", !compact && "rounded-lg border border-border bg-card p-4")}>
      {!compact ? (
        <div className="space-y-0.5">
          <h2 className="text-base font-medium text-foreground">{itemTitle}</h2>
          <p className="font-mono text-xs text-muted-foreground">{item.url}</p>
          <p className="text-xs text-muted-foreground">改完保存后会更新公网该篇。</p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">改完保存后会更新公网该篇。</p>
      )}

      {formError ? <AdminAlert>{formError}</AdminAlert> : null}

      <div className="space-y-3">
        {fields.map((field) => (
          <SchemaFieldInput
            key={field.id}
            field={field}
            value={values[field.id] ?? ""}
            onChange={(next) => setValues((current) => ({ ...current, [field.id]: next }))}
            accountId={accountId}
            brandId={brandId}
            brandSiteId={brandSiteId}
            collectionKey={collection.key}
            itemSlug={item.slug}
            assetBaseUrl={assetBaseUrl}
            publicUrl={publicUrl}
          />
        ))}
      </div>

      <div className="flex justify-end">
        <Button type="button" disabled={saving} onClick={() => void handleSave()}>
          {saving ? "保存中…" : "保存并发布"}
        </Button>
      </div>
    </div>
  );
}

function SchemaFieldInput({
  field,
  value,
  onChange,
  accountId,
  brandId,
  brandSiteId,
  collectionKey,
  itemSlug,
  assetBaseUrl,
  publicUrl,
}: {
  field: ItemSchemaFormField;
  value: string;
  onChange: (value: string) => void;
  accountId: string | number;
  brandId: number;
  brandSiteId: number;
  collectionKey: string;
  itemSlug: string;
  assetBaseUrl: string | null;
  publicUrl: string | null;
}) {
  const id = `item-field-${field.id}`;
  if (isHtmlItemSchemaField(field)) {
    return (
      <BrandSiteItemHtmlField
        id={id}
        label={field.label}
        value={value}
        onChange={onChange}
        assetBaseUrl={assetBaseUrl}
      />
    );
  }

  if (field.control === "url") {
    return (
      <BrandSiteCoverField
        id={id}
        label={field.label}
        value={value}
        onChange={onChange}
        accountId={accountId}
        brandId={brandId}
        brandSiteId={brandSiteId}
        pageSlug={`${collectionKey}/${itemSlug}`}
        publicUrl={publicUrl}
        assetBaseUrl={assetBaseUrl}
      />
    );
  }

  if (field.control === "textarea") {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id}>{field.label}</Label>
        <Textarea
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          rows={8}
          className="font-mono text-sm"
        />
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{field.label}</Label>
      <Input
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete="off"
      />
    </div>
  );
}
